<template>
	<v-container fluid pa-0 my-3>
		<div v-if="loading" class="d-flex justify-center py-8">
			<v-progress-circular indeterminate color="#2D4B95" size="28" width="2" />
		</div>

		<v-alert v-else-if="error" type="error" outlined dense>
			{{ error }}
		</v-alert>

		<template v-else>
			<div v-if="selectedJobType" class="permissions-panel">
				<div class="pm-header">
					<div class="d-flex align-center" style="gap: 12px;">
						<div class="pm-avatar">
							<v-icon color="white" size="20">mdi-shield-key-outline</v-icon>
						</div>
						<div>
							<p class="pm-title mb-0">Permisos por defecto</p>
							<p class="pm-subtitle mb-0">Configura los permisos iniciales de cada cargo</p>
						</div>
					</div>
					<v-select
						v-model="selectedJobTypeId"
						:items="jobTypes"
						item-text="name"
						item-value="id"
						label="Seleccione cargo"
						outlined
						dense
						dark
						hide-details
						class="job-type-select"
						@change="selectJobType"
					/>
				</div>

				<div class="pm-body">
					<div class="pm-intro-row">
						<p class="pm-hint">Los permisos se asignan como valores iniciales al crear una empresa o proyecto. Puedes ajustarlos por cargo sin modificar permisos individuales ya existentes.</p>
						<div class="pm-actions pm-actions-top">
							<v-btn small rounded outlined depressed class="pm-btn-cancel" :disabled="saving" @click="resetSelectedJobType">
								<v-icon left size="16">mdi-restore</v-icon>
								Restablecer
							</v-btn>
							<v-btn small rounded depressed dark color="#2D4B95" :loading="saving" @click="saveDefaults">
								<v-icon left size="16">mdi-content-save-outline</v-icon>
								Guardar cambios
							</v-btn>
						</div>
					</div>

				<v-alert v-if="saveError" type="error" outlined dense>
					{{ saveError }}
				</v-alert>
				<v-alert v-if="saved" type="success" outlined dense>
					Permisos actualizados correctamente.
				</v-alert>

					<section class="pm-level">
						<div class="pm-level-heading">
							<div>
								<p class="pm-section-main-title mb-0"><v-icon size="16" color="#2D4B95" class="mr-1">mdi-domain</v-icon>Nivel empresa</p>
								<p class="pm-hint mb-0">Permisos para la administración de empresas.</p>
							</div>
							<span class="pm-select-all-global" @click="toggleLevel('company')">
								<v-icon size="15" color="#2D4B95" class="mr-1">{{ levelAllSelected('company') ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
								{{ levelAllSelected('company') ? 'Ninguno' : 'Todos' }}
							</span>
						</div>
						<div class="pm-grid">
							<div v-for="section in permissions.company" :key="section.section" class="pm-section">
								<div class="pm-section-header">
									<p class="pm-section-title mb-0">{{ section.section }}</p>
									<span class="pm-select-all-section" @click="toggleSection('company', section)">
										{{ sectionAllSelected('company', section) ? 'Ninguno' : 'Todos' }}
									</span>
								</div>
								<v-checkbox
									v-for="permission in section.permissions"
									:key="permission.key"
									v-model="companyCapabilities[permission.key]"
									:label="permission.label"
									hide-details
									dense
									class="pm-check"
								/>
							</div>
						</div>
					</section>

					<section class="pm-level">
						<div class="pm-level-heading">
							<div>
								<p class="pm-section-main-title mb-0"><v-icon size="16" color="#2D4B95" class="mr-1">mdi-office-building-outline</v-icon>Nivel proyecto</p>
								<p class="pm-hint mb-0">Permisos disponibles dentro de cada proyecto.</p>
							</div>
							<span class="pm-select-all-global" @click="toggleLevel('project')">
								<v-icon size="15" color="#2D4B95" class="mr-1">{{ levelAllSelected('project') ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
								{{ levelAllSelected('project') ? 'Ninguno' : 'Todos' }}
							</span>
						</div>
						<div class="pm-grid">
							<div v-for="section in permissions.project" :key="section.section" class="pm-section">
								<div class="pm-section-header">
									<p class="pm-section-title mb-0">{{ section.section }}</p>
									<span class="pm-select-all-section" @click="toggleSection('project', section)">
										{{ sectionAllSelected('project', section) ? 'Ninguno' : 'Todos' }}
									</span>
								</div>
								<v-checkbox
									v-for="permission in section.permissions"
									:key="permission.key"
									v-model="projectCapabilities[permission.key]"
									:label="permission.label"
									hide-details
									dense
									class="pm-check"
								/>
							</div>
						</div>
					</section>
				</div>

				<div class="pm-actions pm-footer">
					<v-btn small rounded outlined depressed class="pm-btn-cancel" :disabled="saving" @click="resetSelectedJobType">
						<v-icon left size="16">mdi-restore</v-icon>
						Restablecer
					</v-btn>
					<v-btn small rounded depressed dark color="#2D4B95" :loading="saving" @click="saveDefaults">
						<v-icon left size="16">mdi-content-save-outline</v-icon>
						Guardar cambios
					</v-btn>
				</div>
			</div>
		</template>
	</v-container>
</template>

<script>
import {getJobTypeDefaults, updateJobTypeDefaults} from '@/helpers/api/admin'

export default {
	name: 'JobTypePermissions',
	data: () => ({
		loading: false,
		saving: false,
		error: '',
		saveError: '',
		saved: false,
		jobTypes: [],
		permissions: {company: [], project: []},
		selectedJobTypeId: null,
		companyCapabilities: {},
		projectCapabilities: {}
	}),
	computed: {
		selectedJobType(){
			return this.jobTypes.find(jobType => jobType.id === this.selectedJobTypeId)
		}
	},
	mounted(){
		this.fetchDefaults()
	},
	methods: {
		async fetchDefaults(){
			this.loading = true
			this.error = ''
			try{
				const response = await getJobTypeDefaults()
				if(response.code !== 200){
					throw new Error('No fue posible cargar los permisos.')
				}
				this.jobTypes = response.job_types || []
				this.permissions = response.permissions || {company: [], project: []}
				if(this.jobTypes.length){
					this.selectedJobTypeId = this.jobTypes[0].id
					this.selectJobType()
				}
			}catch(error){
				this.error = error.message || 'No fue posible cargar los permisos.'
			}finally{
				this.loading = false
			}
		},
		selectJobType(){
			const jobType = this.selectedJobType
			if(!jobType){
				return
			}
			this.companyCapabilities = this.normalizeCapabilities(jobType.default_company_capabilities, 'company')
			this.projectCapabilities = this.normalizeCapabilities(jobType.default_capabilities, 'project')
			this.saveError = ''
			this.saved = false
		},
		normalizeCapabilities(capabilities, level){
			const values = typeof capabilities === 'string' ? JSON.parse(capabilities || '{}') : (capabilities || {})
			const sections = this.permissions[level] || []
			return sections.reduce((normalized, section) => {
				section.permissions.forEach(permission => {
					normalized[permission.key] = Boolean(values[permission.key])
				})
				return normalized
			}, {})
		},
		setCapability(target, change){
			this[target] = Object.assign({}, this[target], {[change.key]: Boolean(change.value)})
			this.saved = false
		},
		levelKeys(level){
			return (this.permissions[level] || []).reduce((keys, section) => {
				return keys.concat(section.permissions.map(permission => permission.key))
			}, [])
		},
		levelAllSelected(level){
			const keys = this.levelKeys(level)
			const values = level === 'company' ? this.companyCapabilities : this.projectCapabilities
			return keys.length > 0 && keys.every(key => values[key])
		},
		toggleLevel(level){
			const target = level === 'company' ? 'companyCapabilities' : 'projectCapabilities'
			const value = !this.levelAllSelected(level)
			this[target] = this.levelKeys(level).reduce((capabilities, key) => {
				capabilities[key] = value
				return capabilities
			}, {})
			this.saved = false
		},
		sectionAllSelected(level, section){
			const values = level === 'company' ? this.companyCapabilities : this.projectCapabilities
			return section.permissions.length > 0 && section.permissions.every(permission => values[permission.key])
		},
		toggleSection(level, section){
			const target = level === 'company' ? 'companyCapabilities' : 'projectCapabilities'
			const value = !this.sectionAllSelected(level, section)
			this[target] = Object.assign({}, this[target], section.permissions.reduce((capabilities, permission) => {
				capabilities[permission.key] = value
				return capabilities
			}, {}))
			this.saved = false
		},
		resetSelectedJobType(){
			this.selectJobType()
		},
		async saveDefaults(){
			this.saving = true
			this.saveError = ''
			this.saved = false
			try{
				const response = await updateJobTypeDefaults({
					job_type_id: this.selectedJobTypeId,
					default_capabilities: this.projectCapabilities,
					default_company_capabilities: this.companyCapabilities
				})
				if(response.code !== 200){
					throw new Error('No fue posible guardar los permisos.')
				}
				const updatedJobType = response.job_type
				this.jobTypes = this.jobTypes.map(jobType => jobType.id === updatedJobType.id ? updatedJobType : jobType)
				this.selectJobType()
				this.saved = true
			}catch(error){
				this.saveError = error.message || 'No fue posible guardar los permisos.'
			}finally{
				this.saving = false
			}
		}
	}
}
</script>

<style scoped>
.permissions-panel{
	background: #ffffff;
	border-radius: 12px;
	overflow: hidden;
	border: 1px solid #ebeef4;
}
.pm-header{
	background-color: #1C3A7A;
	padding: 16px 20px;
	display: flex;
	align-items: center;
	justify-content: space-between;
}
.pm-avatar{
	width: 38px;
	height: 38px;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.15);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}
.pm-title{font-size: 14px; font-weight: 400; color: #ffffff;}
.pm-subtitle{font-size: 11px; color: rgba(255, 255, 255, 0.6);}
.job-type-select{max-width: 230px;}
.pm-body{padding: 20px 24px 8px;}
.pm-intro-row{
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 20px;
	margin-bottom: 16px;
}
.pm-hint{font-size: 12px; color: #707070; line-height: 1.6; margin: 0;}
.pm-intro-row > .pm-hint{flex: 1; min-width: 0;}
.pm-level{
	border: 1px solid #E0E6F5;
	border-radius: 10px;
	padding: 14px;
	margin-bottom: 14px;
}
.pm-level-heading{
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 10px;
}
.pm-section-main-title{
	display: flex;
	align-items: center;
	font-size: 14px;
	font-weight: 500;
	color: #2D4B95;
}
.pm-select-all-global{
	display: flex;
	align-items: center;
	cursor: pointer;
	padding: 5px 11px;
	border-radius: 20px;
	border: 1px solid #C5D0EA;
	font-size: 12px;
	color: #2D4B95;
	user-select: none;
	white-space: nowrap;
}
.pm-select-all-global:hover{background: #EEF2FB;}
.pm-grid{
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 10px;
}
.pm-section{
	border: 1px solid #E0E6F5;
	border-radius: 10px;
	padding: 10px 14px 12px;
}
.pm-section-header{
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 8px;
}
.pm-section-title{
	font-size: 11px;
	font-weight: 500;
	color: #2D4B95;
	text-transform: uppercase;
	letter-spacing: 0.07em;
	display: flex;
	align-items: center;
}
.pm-select-all-section{
	font-size: 11px;
	color: #2D4B95;
	cursor: pointer;
	text-decoration: underline;
	text-decoration-color: #C5D0EA;
	text-underline-offset: 2px;
	user-select: none;
	white-space: nowrap;
}
.pm-select-all-section:hover{color: #1C3A7A; text-decoration-color: #1C3A7A;}
.pm-check{margin-top: 2px !important;}
.pm-actions{
	display: flex;
	align-items: center;
	gap: 8px;
}
.pm-actions-top{
	justify-content: flex-end;
	flex-shrink: 0;
}
.pm-footer{
	justify-content: flex-end;
	padding: 14px 24px;
	border-top: 1px solid #F0F0F0;
}
.pm-btn-cancel{color: #2D4B95 !important; border-color: #C5D0EA !important;}
/deep/ .pm-check .v-label{font-size: 12px !important; color: #444 !important;}
/deep/ .pm-check .v-input--selection-controls__input{margin-right: 4px;}
/deep/ .pm-check.v-input--checkbox .v-icon{font-size: 18px !important;}
/deep/ .job-type-select .v-select__selection{font-size: 12px; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;}
@media (max-width: 700px){
	.pm-header{align-items: flex-start; gap: 14px; flex-direction: column;}
	.job-type-select{max-width: none; width: 100%;}
	.pm-grid{grid-template-columns: 1fr;}
	.pm-body{padding-left: 14px; padding-right: 14px;}
	.pm-intro-row{align-items: flex-start; flex-direction: column; gap: 10px;}
	.pm-actions-top{width: 100%;}
}
</style>
